import {
  basicLogger,
  LDClient,
  LDContext,
  LDFlagChangeset,
  LDFlagSet,
  LDOptions,
  initialize
} from 'launchdarkly-js-client-sdk';
import { HttpClient } from '@angular/common/http';
import moment from 'moment';
import { Subject } from 'rxjs';

export class LaunchDarklyInterface {
  token: string;
  allowMocks = false;
  flagRAFChange: Subject<boolean> = new Subject<boolean>();
  flagCTG88Change: Subject<string> = new Subject<string>();
  client: LDClient;
  customCache: { [key: string]: string | number | boolean } = {};
  localFlags: {};

  constructor(public http: HttpClient) {}

  initialize(token: string, allowMocks: boolean): Promise<void> {
    this.token = token;
    this.allowMocks = allowMocks;
    this.load();

    if (this.allowMocks) {
      this.loadLocalFlags();
    }

    return this.client.waitUntilReady();
  }

  load(): void {
    const userContext: LDContext = {
      kind: 'user',
      anonymous: true
    };

    const options: LDOptions = {
      logger: basicLogger({ level: 'warn' })
    };
    this.client = initialize(this.token, userContext, options);

    this.client.on('change', (updatedFlags: LDFlagChangeset) => {
      this.updateFlags(updatedFlags);
    });

    this.client.on('failed', () => {
      console.log('LaunchDarkly failed to load.');
    });
  }

  identify(
    userId: string,
    custom?: { [key: string]: string | number | boolean }
  ): Promise<LDFlagSet> {
    var userContext: LDContext;
    if (userId) {
      if (custom) {
        Object.entries(custom).forEach(([key, value]) => {
          if (key != 'created_utc') {
            this.customCache[key] = value;
          }
        });
      }

      userContext = {
        kind: 'user',
        key: userId,
        anonymous: false,
        custom: this.customCache
      };

      if (custom['created_utc']) {
        userContext.created_utc = moment(custom.created_utc as any).valueOf();
      }
    } else {
      userContext = {
        kind: 'user',
        anonymous: true,
        custom: this.customCache
      };
    }

    return this.client.identify(userContext);
  }

  loadLocalFlags(): void {
    this.http.get('/mocks/launchdarkly.json').subscribe((flags: any) => {
      this.localFlags = flags;
    });
  }

  isUsingLocalFlags(): boolean {
    return (
      this.allowMocks &&
      this.localFlags &&
      Object.keys(this.localFlags).length > 0
    );
  }

  track(eventName: string, data?: any): void {
    this.client.track(eventName, data);
  }

  // only need subject and updateFlags() if you need to know if a flag changes after page load
  updateFlags(flags: LDFlagChangeset): void {
    for (const key in flags) {
      if (flags.hasOwnProperty(key)) {
        const flag = flags[key];
        switch (key) {
          case 'ctg-88-nudge-content-on-board-clear':
            this.flagCTG88Change.next(flag.current);
            break;
          default:
        }
      }
    }
  }

  public tryGetFlag(
    key: string
  ): string | boolean | number | string[] | undefined {
    // Debug values for Haseeb
    // console.log(this.allowMocks, this.localFlags, this.localFlags[key], key);
    try {
      if (this.allowMocks && this.localFlags && this.localFlags[key]) {
        return this.localFlags[key];
      }

      let response = this.client.variation(key, undefined);
      if (response === undefined) {
        console.log('LauchDarkly flag not found ' + key);
      }

      return response;
    } catch (error) {
      console.log(error);

      return undefined;
    }
  }

  public tryGetFlagString(key: string): string {
    const flag: string | boolean | number | string[] | undefined =
      this.tryGetFlag(key);
    if (typeof flag === 'string' || typeof flag === 'undefined') {
      return this.tryGetFlag(key) as string;
    }
    console.log(
      "LaunchDarkly type mismatch '" +
        typeof flag +
        "' expected 'string' " +
        key
    );

    return undefined;
  }

  public tryGetFlagBoolean(key: string): boolean {
    const flag: string | boolean | number | string[] | undefined =
      this.tryGetFlag(key);
    if (typeof flag === 'boolean' || typeof flag === 'undefined') {
      return this.tryGetFlag(key) as boolean;
    }
    console.log(
      "LaunchDarkly type mismatch '" +
        typeof flag +
        "' expected 'boolean' " +
        key
    );

    return undefined;
  }

  public tryGetFlagNumber(key: string): number {
    const flag: string | boolean | number | string[] | undefined =
      this.tryGetFlag(key);
    if (typeof flag === 'number' || typeof flag === 'undefined') {
      return this.tryGetFlag(key) as number;
    }
    console.log(
      "LaunchDarkly type mismatch '" +
        typeof flag +
        "' expected 'number' " +
        key
    );

    return undefined;
  }

  public tryGetFlagJSON(key: string): string[] {
    const flag: string | boolean | number | string[] | undefined =
      this.tryGetFlag(key);
    if (
      (typeof flag === 'object' && Array.isArray(flag)) ||
      typeof flag === 'undefined'
    ) {
      return this.tryGetFlag(key) as Array<string>;
    }
    console.log(
      "LaunchDarkly type mismatch '" + typeof flag + "' expected 'JSON' " + key
    );

    return undefined;
  }

  getRW4812WebVOE(): boolean {
    return this.tryGetFlagBoolean('rw-4812-web-voe');
  }
}
